'use client';

import { createContext, use, useRef, useState } from 'react';

interface AnnouncerContextValue {
  announce: (message: string) => void;
}

const AnnouncerContext: React.Context<AnnouncerContextValue | null> =
  createContext<AnnouncerContextValue | null>(null);

/**
 * Hook to announce messages to screen readers via an aria-live region.
 * Must be used within an AnnouncerProvider.
 */
export function useAnnouncer(): AnnouncerContextValue {
  const context = use(AnnouncerContext);
  if (context === null) {
    throw new Error('useAnnouncer must be used within an AnnouncerProvider');
  }
  return context;
}

interface AnnouncerProviderProps {
  children: React.ReactNode;
}

/**
 * Provides an aria-live="polite" region for screen reader announcements.
 * Place once near the root of the component tree.
 */
export function AnnouncerProvider({ children }: AnnouncerProviderProps): React.ReactNode {
  const [message, setMessage] = useState<string>('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function announce(text: string): void {
    // Clear previous message first so re-announcing the same text works
    setMessage('');

    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }

    // Use a microtask delay so the DOM sees the empty string before the new message
    timeoutRef.current = setTimeout((): void => {
      setMessage(text);
    }, 100);
  }

  const value: AnnouncerContextValue = { announce };

  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      <output aria-live="polite" aria-atomic="true" className="sr-only">
        {message}
      </output>
    </AnnouncerContext.Provider>
  );
}
